import { gettext } from '@eventbrite/i18n';
import { Typography } from '@eventbrite/marmalade';
import React from 'react';
import type { InfiniteCalendarSeriesDispatcher } from '../../actions/infiniteCalendarSeries';
import { PageTemplate, TemplateProps } from '../../containers/template';
import {
    AccordionSection,
    ScrollableCalendarData,
    type InfiniteCalendarSeriesNamespace,
} from '../../state/infiniteCalendarSeries';
import {
    AccordionPanel,
    CalendarContainer,
    DateFormatType,
    getFormattedTime,
} from '../TimedEntrySeries';
import { DateSvg } from '../TimedEntrySeries/assests/DateSvg';
import { TimeSvg } from '../TimedEntrySeries/assests/TimeSvg';
import { TimeSlotContainer } from '../TimedEntrySeries/TimeSlotContainer';
import customStyles from './InfiniteCalendarSeries.module.scss';
import {
    getFormattedDateOrEmpty,
    getMonthAndYearFromDate,
    isAccordionSectionOpen,
} from './utils';

export interface InfiniteCalendarSeriesProps
    extends InfiniteCalendarSeriesNamespace,
        InfiniteCalendarSeriesDispatcher {}

export const InfiniteCalendarSeries: React.FC<InfiniteCalendarSeriesProps> = ({
    activeSection,
    isAppRebrandActive,
    locale,
    selectedDate,
    templateProps,
    timezone,
    parentEventInfo,
    selectedTime,
    onDateSelect,
    onMonthLoad,
    onTimeSelect,
    onToggleSection,
    calendarData,
    timeSlots,
}) => {
    const displayDate: string =
        activeSection === AccordionSection.date
            ? ''
            : getFormattedDateOrEmpty(
                  selectedDate,
                  DateFormatType.long,
                  locale,
              );
    const displayTime: string =
        activeSection === AccordionSection.time || !selectedTime
            ? ''
            : getFormattedTime(selectedTime, locale, timezone);

    const handleSectionToggle = (selectedSection: AccordionSection) => {
        onToggleSection(selectedSection as AccordionSection);
    };

    return (
        <PageTemplate
            notificationOptions={[]}
            templateProps={templateProps as TemplateProps}
            isAppRebrandActive={isAppRebrandActive}
            containerClassName={customStyles.pageTemplateContainer}
        >
            <div className={customStyles.container}>
                <header className={customStyles.headerContainer}>
                    <Typography
                        as="h2"
                        variant="heading-sm"
                        color="neutral-900"
                    >
                        {gettext('Choose options')}
                    </Typography>
                </header>

                <div className={customStyles.contentContainer}>
                    <AccordionPanel
                        title={gettext('Date').toString()}
                        isExpanded={isAccordionSectionOpen(
                            activeSection,
                            AccordionSection.date,
                        )}
                        onClick={() => {
                            handleSectionToggle(AccordionSection.date);
                        }}
                        icon={<DateSvg />}
                        displayDateTime={displayDate}
                    >
                        <CalendarContainer
                            calendarData={
                                calendarData as ScrollableCalendarData
                            }
                            onMonthLoad={(monthAndYear) =>
                                onMonthLoad(monthAndYear, timezone as string)
                            }
                            onDateSelect={onDateSelect}
                            initialSelectedDate={getFormattedDateOrEmpty(
                                selectedDate,
                                DateFormatType.short,
                                locale,
                            )}
                            locale={locale}
                            firstMonth={{
                                ...getMonthAndYearFromDate(
                                    parentEventInfo.startUTCDate,
                                ),
                            }}
                            lastMonth={{
                                ...getMonthAndYearFromDate(
                                    parentEventInfo.endUTCDate,
                                ),
                            }}
                        />
                    </AccordionPanel>
                    <AccordionPanel
                        title={gettext('Time').toString()}
                        isExpanded={isAccordionSectionOpen(
                            activeSection,
                            AccordionSection.time,
                        )}
                        onClick={() =>
                            handleSectionToggle(AccordionSection.time)
                        }
                        icon={<TimeSvg />}
                        displayDateTime={displayTime}
                    >
                        <TimeSlotContainer
                            timeSlotData={timeSlots || []}
                            timezone={timezone}
                            onTimeSelect={onTimeSelect}
                            locale={locale}
                        />
                    </AccordionPanel>
                </div>
            </div>
        </PageTemplate>
    );
};
